import { getEventId } from '../../selectors/event';
import { TaxExemptionStatus } from '../../containers/checkout/constants';
import { CannedQuestionName } from '../../api/models/survey';
import isEmpty from 'lodash/isEmpty';
import { validateTaxId, validateQstTaxId } from '../../api/taxExemptions';
import { updateOrderAction, updateTaxExemptionMessageAction } from '../order';
import { PaymentMethodType } from '../../models/paymentMethods/enums';
import { saveOrderWithTaxExemption } from './saveOrderWithTaxExemption';
import { getSurvey } from '../../selectors/survey';
import { getBillingZipCode } from '../../selectors/forms';

const getTaxExemptionFromState = (state) => {
    const {
        order: { costs: { tax = {}, displayTax, cachedDisplayTax } = {} },
    } = state;

    const taxBeforeExemption = tax.taxBeforeExemption;

    return {
        taxBeforeExemption,
        displayTax,
        tax,
        cachedDisplayTax,
    };
};

const applyQuebecQSTTaxExemption = async (dispatch, getState) => {
    const { taxBeforeExemption, cachedDisplayTax } = getTaxExemptionFromState(
        getState(),
    );

    const orderUpdated = await dispatch(saveOrderWithTaxExemption());
    const taxValue = taxBeforeExemption ?? cachedDisplayTax.tax.value;

    orderUpdated.response.costs.displayTax = {
        // For QST, we use the cached value + current tax value
        ...cachedDisplayTax,
        tax: orderUpdated.response.costs.tax,
    }; // re-use the value from the init order
    orderUpdated.response.costs.tax.taxBeforeExemption = taxValue;

    dispatch(updateOrderAction(orderUpdated.response));
    dispatch(
        updateTaxExemptionMessageAction(
            TaxExemptionStatus.validTaxIdAndTaxExemption,
        ),
    );
};

const applyNonGTSTaxExemption = async (dispatch, getState) => {
    const { taxBeforeExemption, displayTax, tax } = getTaxExemptionFromState(
        getState(),
    );

    const hasTaxExemptionApplied = taxBeforeExemption;

    if (hasTaxExemptionApplied) {
        dispatch(
            updateTaxExemptionMessageAction(
                TaxExemptionStatus.validTaxIdAndTaxExemption,
            ),
        );
        return;
    }

    if (!displayTax) {
        dispatch(
            updateTaxExemptionMessageAction(
                TaxExemptionStatus.validTaxIdWithoutTaxExemption,
            ),
        );
        return;
    }

    const orderUpdated = await dispatch(saveOrderWithTaxExemption());

    orderUpdated.response.costs.tax.displayTax = displayTax;
    orderUpdated.response.costs.tax.taxBeforeExemption = tax.value;

    dispatch(updateOrderAction(orderUpdated.response));
    dispatch(
        updateTaxExemptionMessageAction(
            TaxExemptionStatus.validTaxIdAndTaxExemption,
        ),
    );
};

const applyTaxExemption = (dispatch, getState) =>
    getSurvey(getState()).hasQuebecQSTTaxQuestion
        ? applyQuebecQSTTaxExemption(dispatch, getState)
        : applyNonGTSTaxExemption(dispatch, getState);

const revokeTaxExemption = () => async (dispatch, getState) => {
    const { taxBeforeExemption } = getTaxExemptionFromState(getState());
    const revokesExemption = taxBeforeExemption;

    if (!revokesExemption) {
        return;
    }

    const orderUpdated = await dispatch(saveOrderWithTaxExemption());

    orderUpdated.response.costs.tax.taxBeforeExemption = null;

    dispatch(updateOrderAction(orderUpdated.response));
};

const checkQstTaxesFactory =
    (isLatestCheckQstTaxesCall) => async (dispatch, getState) => {
        const state = getState();
        const {
            form: { checkoutForm: { values: { buyer = {} } = {} } = {} },
        } = state;

        const taxId = buyer[CannedQuestionName.taxRegistrationId];
        const residenceZipCode = buyer[CannedQuestionName.qstZipCode];
        const billingZipCode = getBillingZipCode(state);

        if (isEmpty(residenceZipCode)) {
            dispatch(updateTaxExemptionMessageAction(TaxExemptionStatus.init));
            return;
        }

        dispatch(updateTaxExemptionMessageAction(TaxExemptionStatus.verifying));

        const { is_exempt, is_tax_id_valid } = await validateQstTaxId({
            eventId: getEventId(getState()),
            residenceZipCode,
            billingZipCode,
            taxId,
        });

        if (!isLatestCheckQstTaxesCall()) {
            return;
        }

        if (!is_exempt) {
            await dispatch(revokeTaxExemption());

            const taxExemptionStatus = is_tax_id_valid
                ? TaxExemptionStatus.validTaxIdWithoutTaxExemption
                : TaxExemptionStatus.invalidTaxId;

            dispatch(updateTaxExemptionMessageAction(taxExemptionStatus));

            return;
        }

        try {
            await dispatch(applyTaxExemption);
        } catch (error) {
            await dispatch(revokeTaxExemption());

            dispatch(
                updateTaxExemptionMessageAction(
                    TaxExemptionStatus.validTaxIdWithoutTaxExemption,
                ),
            );
        }
    };

let lastCheckQstTaxesTask = {
    id: 0,
    promise: Promise.resolve(),
};

const checkQstTaxesSequentially = () => (dispatch) => {
    const id = lastCheckQstTaxesTask.id + 1;
    const isLatestCheckQstTaxesCall = () => id === lastCheckQstTaxesTask.id;

    const promise = lastCheckQstTaxesTask.promise.then(() =>
        isLatestCheckQstTaxesCall()
            ? dispatch(checkQstTaxesFactory(isLatestCheckQstTaxesCall)).catch(
                  () => {
                      /* no op */
                  },
              )
            : Promise.resolve(),
    );

    const nextCheckQstTaxesTask = {
        id,
        promise,
    };

    lastCheckQstTaxesTask = nextCheckQstTaxesTask;

    return promise;
};

/**
 * @param {TaxExemptionStatus} status
 * @returns {(dispatch: Dispatch<any>) => Promise<void>}
 */
const revokeTaxExemptionWithMessage = (status) => async (dispatch) => {
    await dispatch(revokeTaxExemption());
    dispatch(updateTaxExemptionMessageAction(status));
};

/**
 * Validates current tax ID and country and updates the message provided to the user.
 */
export const checkTaxExemptionApplies = () => async (dispatch, getState) => {
    const state = getState();
    const {
        form: {
            checkoutForm: { values: { buyer = {}, paymentMethod } = {} } = {},
        },
    } = state;

    const { hasQuebecQSTTaxQuestion: isQuebecQSTEvent } = getSurvey(state);
    if (isQuebecQSTEvent) {
        return dispatch(checkQstTaxesSequentially());
    }

    const isPayingWithPayPal =
        paymentMethod === PaymentMethodType.NONCE_BRAINTREE;

    if (isPayingWithPayPal) {
        return;
    }

    const taxId = buyer[CannedQuestionName.taxRegistrationId];

    const { country: countryTaxAddress = '' } =
        buyer[CannedQuestionName.businessTaxAddress] || {};

    const isBusiness = buyer[CannedQuestionName.isBusiness] === '1';

    const shouldRevokeTaxExemptions = !isBusiness || isEmpty(taxId);

    if (shouldRevokeTaxExemptions) {
        return dispatch(revokeTaxExemptionWithMessage(TaxExemptionStatus.init));
    }

    dispatch(updateTaxExemptionMessageAction(TaxExemptionStatus.verifying));

    const { is_tax_exempt, valid } = await validateTaxId(
        getEventId(getState()),
        countryTaxAddress,
        taxId,
    );

    if (!valid) {
        return dispatch(
            revokeTaxExemptionWithMessage(TaxExemptionStatus.invalidTaxId),
        );
    }

    if (!is_tax_exempt) {
        return dispatch(
            revokeTaxExemptionWithMessage(
                TaxExemptionStatus.validTaxIdWithoutTaxExemption,
            ),
        );
    }

    try {
        await dispatch(applyTaxExemption);
    } catch (error) {
        return dispatch(
            revokeTaxExemptionWithMessage(
                TaxExemptionStatus.validTaxIdWithoutTaxExemption,
            ),
        );
    }
};
